import { routeHref } from "../../app/router";
import {
  ICONS,
  type ControlCommand,
  type ViewControl,
  type ViewElement,
  type ViewManifest,
} from "../../shared/api/generated/contracts";
import { parseControlCommand } from "../../shared/validation/manifest";
import type { RendererContext } from "./registry";

/** `config.command` of a button element, falling back to the legacy target. */
export function commandOfButton(
  element: ViewElement,
): ControlCommand | undefined {
  const command = parseControlCommand(element.config?.command);
  if (command) return command;
  const target = element.config?.target_view_id;
  return typeof target === "string"
    ? { kind: "open_view", view_id: target }
    : undefined;
}

/** `open_view` runs in the renderer; the rest need the host's `onCommand`. */
export function isCommandAvailable(
  command: ControlCommand,
  context: Pick<RendererContext, "onCommand">,
): boolean {
  return command.kind === "open_view" || context.onCommand !== undefined;
}

export function runCommand(
  command: ControlCommand,
  context: RendererContext,
): void {
  if (command.kind === "open_view") {
    if (context.abilityId)
      context.navigate(
        routeHref({
          kind: "ability",
          abilityId: context.abilityId,
          viewId: command.view_id,
        }),
      );
    return;
  }
  context.onCommand?.(command);
}

/** Every control a view declares, wherever it sits. */
export function controlsOf(view: ViewManifest): ViewControl[] {
  return [
    ...(view.header_actions ?? []),
    ...(view.fab ? [view.fab] : []),
    ...view.sections.flatMap((section) => (section.fab ? [section.fab] : [])),
  ];
}

/** Collections the view can create a record in, by any of its controls. */
export function collectionsWithCreateControl(view: ViewManifest): Set<string> {
  const commands = [
    ...controlsOf(view).map((control) => control.command),
    ...view.sections.flatMap((section) =>
      section.elements.flatMap((element) =>
        element.kind === "button" ? [element.config?.command] : [],
      ),
    ),
  ];
  const collections = new Set<string>();
  for (const raw of commands) {
    const command = parseControlCommand(raw);
    if (command?.kind === "create_record")
      collections.add(command.collection_id);
  }
  return collections;
}

const COMMAND_KINDS: readonly string[] = [
  "create_record",
  "open_view",
  "print",
];

/** A control the renderer knows how to draw and run. */
export function isKnownControl(control: ViewControl): boolean {
  return (
    COMMAND_KINDS.includes((control.command as { kind?: string }).kind ?? "") &&
    (control.icon === undefined || ICONS.includes(control.icon))
  );
}
